import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  Dimensions,
  TouchableOpacity,
  Animated,
} from 'react-native';
import {
  Text,
  Card,
  useTheme,
  Chip,
  IconButton,
  Surface,
} from 'react-native-paper';
import type { MasteryState } from '@coffeeplexity/shared';
import { SkillMasteryDisplay } from './SkillMasteryDisplay';
import { MasteryComparisonTools } from './MasteryComparisonTools';

const { width: screenWidth } = Dimensions.get('window');

export interface SkillCategory {
  id: string;
  name: string;
  description: string;
  color: string;
  icon: string;
  skills: string[];
}

interface MasteryVisualizationProps {
  masteryStates: MasteryState[];
  skillCategories: SkillCategory[];
  onSkillSelect: (skillId: string) => void;
  animationEnabled?: boolean;
  showComparison?: boolean;
  userId?: string;
  style?: any;
}

type ViewMode = 'grid' | 'list' | 'hierarchy' | 'comparison';
type FilterMode = 'all' | 'improving' | 'stable' | 'declining';

export function MasteryVisualization({
  masteryStates,
  skillCategories,
  onSkillSelect,
  animationEnabled = true,
  showComparison = false,
  userId,
  style,
}: MasteryVisualizationProps) {
  const theme = useTheme();
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );

  // Group mastery states by category
  const masteryByCategory = useMemo(() => {
    const grouped: Record<string, MasteryState[]> = {};

    skillCategories.forEach(category => {
      grouped[category.id] = masteryStates.filter(
        mastery =>
          category.skills.includes(mastery.skillId) ||
          mastery.skillCategory === category.id
      );
    });

    // Add uncategorized skills
    const categorizedSkillIds = new Set(
      skillCategories.flatMap(cat => cat.skills)
    );
    const uncategorized = masteryStates.filter(
      mastery => !categorizedSkillIds.has(mastery.skillId)
    );

    if (uncategorized.length > 0) {
      grouped['uncategorized'] = uncategorized;
    }

    return grouped;
  }, [masteryStates, skillCategories]);

  // Filter mastery states based on current filter
  const filteredMasteryStates = useMemo(() => {
    let filtered = masteryStates;

    if (filterMode !== 'all') {
      filtered = filtered.filter(mastery => mastery.trend === filterMode);
    }

    if (selectedCategory) {
      const categorySkills =
        skillCategories.find(cat => cat.id === selectedCategory)?.skills || [];
      filtered = filtered.filter(
        mastery =>
          categorySkills.includes(mastery.skillId) ||
          mastery.skillCategory === selectedCategory
      );
    }

    return filtered;
  }, [masteryStates, filterMode, selectedCategory, skillCategories]);

  // Calculate category statistics
  const categoryStats = useMemo(() => {
    const stats: Record<
      string,
      {
        averageMastery: number;
        skillCount: number;
        improvingCount: number;
        decliningCount: number;
      }
    > = {};

    Object.entries(masteryByCategory).forEach(([categoryId, masteries]) => {
      const avgMastery =
        masteries.length > 0
          ? masteries.reduce((sum, m) => sum + m.masteryLevel, 0) /
            masteries.length
          : 0;

      stats[categoryId] = {
        averageMastery: avgMastery,
        skillCount: masteries.length,
        improvingCount: masteries.filter(m => m.trend === 'improving').length,
        decliningCount: masteries.filter(m => m.trend === 'declining').length,
      };
    });

    return stats;
  }, [masteryByCategory]);

  const handleCategoryToggle = useCallback((categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  }, []);

  const handleCategorySelect = useCallback((categoryId: string | null) => {
    setSelectedCategory(categoryId);
  }, []);

  const renderCategoryHeader = (category: SkillCategory, stats: any) => (
    <TouchableOpacity
      key={category.id}
      onPress={() => handleCategoryToggle(category.id)}
      style={[
        styles.categoryHeader,
        { backgroundColor: theme.colors.surfaceVariant },
      ]}
    >
      <View style={styles.categoryHeaderContent}>
        <View style={styles.categoryInfo}>
          <View
            style={[styles.categoryIcon, { backgroundColor: category.color }]}
          >
            <Text style={styles.categoryIconText}>{category.icon}</Text>
          </View>
          <View style={styles.categoryDetails}>
            <Text variant='titleMedium' style={styles.categoryName}>
              {category.name}
            </Text>
            <Text variant='bodySmall' style={styles.categoryDescription}>
              {category.description}
            </Text>
          </View>
        </View>

        <View style={styles.categoryStats}>
          <Text variant='bodySmall' style={styles.statText}>
            {stats.skillCount} skills
          </Text>
          <Text
            variant='bodySmall'
            style={[
              styles.statText,
              { color: getMasteryColor(stats.averageMastery) },
            ]}
          >
            {Math.round(stats.averageMastery * 100)}% avg
          </Text>
          {stats.improvingCount > 0 && (
            <Text
              variant='bodySmall'
              style={[styles.statText, { color: theme.colors.primary }]}
            >
              ↗ {stats.improvingCount}
            </Text>
          )}
        </View>

        <IconButton
          icon={
            expandedCategories.has(category.id) ? 'chevron-up' : 'chevron-down'
          }
          size={20}
        />
      </View>
    </TouchableOpacity>
  );

  const renderGridView = () => (
    <ScrollView style={styles.scrollContainer}>
      <View style={styles.gridContainer}>
        {filteredMasteryStates.map(mastery => (
          <View key={mastery.id} style={styles.gridItem}>
            <SkillMasteryDisplay
              masteryState={mastery}
              onPress={() => onSkillSelect(mastery.skillId)}
              animationEnabled={animationEnabled}
              compact={true}
            />
          </View>
        ))}
      </View>
    </ScrollView>
  );

  const renderListView = () => (
    <ScrollView style={styles.scrollContainer}>
      {filteredMasteryStates.map(mastery => (
        <View key={mastery.id} style={styles.listItem}>
          <SkillMasteryDisplay
            masteryState={mastery}
            onPress={() => onSkillSelect(mastery.skillId)}
            animationEnabled={animationEnabled}
            compact={false}
          />
        </View>
      ))}
    </ScrollView>
  );

  const renderHierarchyView = () => (
    <ScrollView style={styles.scrollContainer}>
      {skillCategories.map(category => {
        const categoryMasteries = masteryByCategory[category.id] || [];
        const stats = categoryStats[category.id];

        if (categoryMasteries.length === 0) return null;

        return (
          <View key={category.id} style={styles.categorySection}>
            {renderCategoryHeader(category, stats)}

            {expandedCategories.has(category.id) && (
              <View style={styles.categoryContent}>
                {categoryMasteries.map(mastery => (
                  <View key={mastery.id} style={styles.hierarchyItem}>
                    <SkillMasteryDisplay
                      masteryState={mastery}
                      onPress={() => onSkillSelect(mastery.skillId)}
                      animationEnabled={animationEnabled}
                      compact={false}
                    />
                  </View>
                ))}
              </View>
            )}
          </View>
        );
      })}
    </ScrollView>
  );

  const renderComparisonView = () => (
    <MasteryComparisonTools
      masteryStates={masteryStates}
      userId={userId}
      onSkillSelect={onSkillSelect}
    />
  );

  const renderContent = () => {
    switch (viewMode) {
      case 'grid':
        return renderGridView();
      case 'list':
        return renderListView();
      case 'hierarchy':
        return renderHierarchyView();
      case 'comparison':
        return renderComparisonView();
      default:
        return renderGridView();
    }
  };

  return (
    <Surface style={[styles.container, style]} elevation={1}>
      {/* Header Controls */}
      <View style={styles.header}>
        <Text variant='headlineSmall' style={styles.title}>
          Skill Mastery
        </Text>

        <View style={styles.controls}>
          {/* View Mode Selector */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.viewModeContainer}
          >
            {(['grid', 'list', 'hierarchy'] as ViewMode[]).map(mode => (
              <Chip
                key={mode}
                selected={viewMode === mode}
                onPress={() => setViewMode(mode)}
                style={styles.viewModeChip}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </Chip>
            ))}
            {showComparison && (
              <Chip
                selected={viewMode === 'comparison'}
                onPress={() => setViewMode('comparison')}
                style={styles.viewModeChip}
              >
                Compare
              </Chip>
            )}
          </ScrollView>

          {/* Filter Controls */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.filterContainer}
          >
            {(['all', 'improving', 'stable', 'declining'] as FilterMode[]).map(
              mode => (
                <Chip
                  key={mode}
                  selected={filterMode === mode}
                  onPress={() => setFilterMode(mode)}
                  style={styles.filterChip}
                  compact
                >
                  {mode === 'all'
                    ? 'All'
                    : mode.charAt(0).toUpperCase() + mode.slice(1)}
                </Chip>
              )
            )}
          </ScrollView>
        </View>
      </View>

      {/* Category Filter */}
      {viewMode !== 'hierarchy' && (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.categoryFilter}
        >
          <Chip
            selected={selectedCategory === null}
            onPress={() => handleCategorySelect(null)}
            style={styles.categoryChip}
          >
            All Categories
          </Chip>
          {skillCategories.map(category => (
            <Chip
              key={category.id}
              selected={selectedCategory === category.id}
              onPress={() => handleCategorySelect(category.id)}
              style={[
                styles.categoryChip,
                {
                  backgroundColor:
                    selectedCategory === category.id
                      ? category.color
                      : undefined,
                },
              ]}
            >
              {category.icon} {category.name}
            </Chip>
          ))}
        </ScrollView>
      )}

      {/* Content */}
      <View style={styles.content}>{renderContent()}</View>

      {/* Summary Stats */}
      <View style={styles.summary}>
        <Text variant='bodySmall' style={styles.summaryText}>
          {filteredMasteryStates.length} skills • Avg:{' '}
          {Math.round(
            (filteredMasteryStates.reduce((sum, m) => sum + m.masteryLevel, 0) /
              Math.max(filteredMasteryStates.length, 1)) *
              100
          )}
          % • Improving:{' '}
          {filteredMasteryStates.filter(m => m.trend === 'improving').length}
        </Text>
      </View>
    </Surface>
  );
}

// Helper function to get mastery level color
function getMasteryColor(masteryLevel: number): string {
  if (masteryLevel >= 0.8) return '#4CAF50'; // Green
  if (masteryLevel >= 0.6) return '#8BC34A'; // Light Green
  if (masteryLevel >= 0.4) return '#FFC107'; // Amber
  if (masteryLevel >= 0.2) return '#FF9800'; // Orange
  return '#F44336'; // Red
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    margin: 16,
    borderRadius: 12,
  },
  header: {
    padding: 16,
    paddingBottom: 8,
  },
  title: {
    marginBottom: 12,
    fontWeight: '600',
  },
  controls: {
    gap: 8,
  },
  viewModeContainer: {
    flexDirection: 'row',
  },
  viewModeChip: {
    marginRight: 8,
  },
  filterContainer: {
    flexDirection: 'row',
  },
  filterChip: {
    marginRight: 8,
  },
  categoryFilter: {
    paddingHorizontal: 16,
    paddingBottom: 8,
  },
  categoryChip: {
    marginRight: 8,
  },
  content: {
    flex: 1,
  },
  scrollContainer: {
    flex: 1,
  },
  gridContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: 8,
    justifyContent: 'space-between',
  },
  gridItem: {
    width: (screenWidth - 64) / 2,
    marginBottom: 8,
  },
  listItem: {
    marginHorizontal: 16,
    marginBottom: 8,
  },
  categorySection: {
    marginBottom: 16,
  },
  categoryHeader: {
    marginHorizontal: 16,
    borderRadius: 8,
    overflow: 'hidden',
  },
  categoryHeaderContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
  },
  categoryInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  categoryIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  categoryIconText: {
    fontSize: 18,
    color: 'white',
  },
  categoryDetails: {
    flex: 1,
  },
  categoryName: {
    fontWeight: '600',
  },
  categoryDescription: {
    opacity: 0.7,
    marginTop: 2,
  },
  categoryStats: {
    alignItems: 'flex-end',
    marginRight: 8,
  },
  statText: {
    opacity: 0.8,
  },
  categoryContent: {
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 8,
  },
  hierarchyItem: {
    marginBottom: 8,
  },
  summary: {
    padding: 16,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
  },
  summaryText: {
    textAlign: 'center',
    opacity: 0.7,
  },
});
